// =============================================================================
// HistorialPagosPanel.tsx
// Panel lateral con dos pestañas:
//   · Facturas    → comprobantes/facturas de Stripe (cobros)
//   · Movimientos → altas, bajas y cambios de plan y de trabajadores
// Mismo patrón visual que "Notificaciones": fondo oscurecido + panel
// deslizante desde la derecha, con filtros por pestaña.
// =============================================================================

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Search,
  FileText,
  Download,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCw,
  UserPlus,
  UserMinus,
  Calendar,
  History,
  XCircle,
  CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type {
  Comprobante,
  MovimientoSuscripcion,
} from '@/components/hooks/useSubscripcion';

type Pestana = 'facturas' | 'movimientos';
type FiltroTipo = 'todos' | 'plan' | 'trabajador';

const formatFecha = (fecha: string | null | undefined) => {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatFechaHora = (fecha: string | null | undefined) => {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Etiqueta legible + icono + color para cada tipo de movimiento.
// Cubre los tipos de historial_planes_contratados y
// empresa_trabajadores_historial.
const infoMovimiento = (
  tipo: string
): { label: string; Icon: typeof ArrowUpCircle; cls: string } => {
  switch (tipo) {
    case 'ALTA':
      return { label: 'Alta', Icon: ArrowUpCircle, cls: 'text-emerald-600 dark:text-emerald-400' };
    case 'BAJA':
      return { label: 'Baja', Icon: ArrowDownCircle, cls: 'text-red-600 dark:text-red-400' };
    case 'HABILITADO':
      return { label: 'Acceso habilitado', Icon: UserPlus, cls: 'text-emerald-600 dark:text-emerald-400' };
    case 'DESHABILITADO':
      return { label: 'Acceso deshabilitado', Icon: UserMinus, cls: 'text-amber-600 dark:text-amber-400' };
    case 'CAMBIO_PLAN':
      return { label: 'Cambio de plan', Icon: RefreshCw, cls: 'text-blue-600 dark:text-blue-400' };
    case 'CANCELACION':
      return { label: 'Cancelación', Icon: XCircle, cls: 'text-red-600 dark:text-red-400' };
    case 'REACTIVACION':
      return { label: 'Reactivación', Icon: CheckCircle, cls: 'text-emerald-600 dark:text-emerald-400' };
    case 'RENOVACION':
      return { label: 'Renovación', Icon: RefreshCw, cls: 'text-emerald-600 dark:text-emerald-400' };
    case 'AUTO_ASIGNACION_GRATUITO':
      return { label: 'Asignación gratuita', Icon: Calendar, cls: 'text-[#288E7F]' };
    default:
      return { label: tipo, Icon: History, cls: 'text-gray-500 dark:text-gray-400' };
  }
};

interface HistorialPagosPanelProps {
  open: boolean;
  onClose: () => void;
  comprobantes: Comprobante[];
  movimientos: MovimientoSuscripcion[];
  loading: boolean;
  onDescargar: (c: Comprobante) => void;
  onRefrescar: () => void;
  onRefrescarMovimientos?: () => void;
}

export default function HistorialPagosPanel({
  open,
  onClose,
  comprobantes,
  movimientos,
  loading,
  onDescargar,
  onRefrescar,
  onRefrescarMovimientos,
}: HistorialPagosPanelProps) {
  const [pestana, setPestana] = useState<Pestana>('facturas');
  const [filtroTipo, setFiltroTipo] = useState<FiltroTipo>('todos');
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    if (open) {
      onRefrescar();
      onRefrescarMovimientos?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Al cambiar de pestaña se resetea el filtro de tipo y la búsqueda, para
  // no arrastrar un filtro que no aplica a la otra pestaña.
  useEffect(() => {
    setFiltroTipo('todos');
    setBusqueda('');
  }, [pestana]);

  // ── Facturas filtradas ────────────────────────────────────────────────────
  const facturasFiltradas = comprobantes.filter((c) => {
    const esPlan = c.concepto.startsWith('Plan');
    if (filtroTipo === 'plan' && !esPlan) return false;
    if (filtroTipo === 'trabajador' && esPlan) return false;

    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      return (
        c.concepto.toLowerCase().includes(q) ||
        c.periodo.toLowerCase().includes(q) ||
        c.numero.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // ── Movimientos filtrados ─────────────────────────────────────────────────
  const movimientosFiltrados = movimientos.filter((m) => {
    if (filtroTipo === 'plan' && m.ambito !== 'plan') return false;
    if (filtroTipo === 'trabajador' && m.ambito !== 'trabajador') return false;

    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      const texto = [
        m.tipo_movimiento,
        m.detalle ?? '',
        m.trabajador ?? '',
        m.observaciones ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return texto.includes(q);
    }
    return true;
  });

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Fondo oscurecido */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white dark:bg-[#111214] z-50 shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-white/10">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Historial
              </h2>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Pestañas */}
            <div className="flex px-5 pt-3 gap-1 border-b border-gray-100 dark:border-white/10">
              {(
                [
                  { key: 'facturas', label: 'Facturas' },
                  { key: 'movimientos', label: 'Movimientos' },
                ] as { key: Pestana; label: string }[]
              ).map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPestana(p.key)}
                  className={`relative text-sm font-medium px-4 py-2.5 transition-colors ${
                    pestana === p.key
                      ? 'text-[#103254] dark:text-white'
                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                  }`}
                >
                  {p.label}
                  {pestana === p.key && (
                    <motion.div
                      layoutId="pestana-activa"
                      className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#103254] dark:bg-[#288E7F] rounded-full"
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Filtros (comunes a ambas pestañas) */}
            <div className="px-5 py-3 border-b border-gray-100 dark:border-white/10 space-y-2.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder={
                    pestana === 'facturas'
                      ? 'Buscar por concepto, periodo o nº...'
                      : 'Buscar por tipo, plan o trabajador...'
                  }
                  className="w-full rounded-lg pl-8 pr-3 py-2 text-xs bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-[#103254] dark:focus:ring-white/30"
                />
              </div>

              <div className="flex gap-1.5">
                {(
                  [
                    { key: 'todos', label: 'Todos' },
                    { key: 'plan', label: 'Plan' },
                    { key: 'trabajador', label: 'Trabajadores' },
                  ] as { key: FiltroTipo; label: string }[]
                ).map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFiltroTipo(f.key)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                      filtroTipo === f.key
                        ? 'bg-[#103254] dark:bg-[#288E7F] text-white'
                        : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/20'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Lista */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loading ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-400" />
                </div>
              ) : pestana === 'facturas' ? (
                // ── PESTAÑA FACTURAS ──────────────────────────────────────────
                facturasFiltradas.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <FileText className="w-8 h-8 text-gray-200 dark:text-gray-700 mb-2" />
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      {comprobantes.length === 0
                        ? 'No hay comprobantes disponibles todavía.'
                        : 'No hay resultados para ese filtro.'}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {facturasFiltradas.map((c) => (
                      <div
                        key={c.id_comprobante}
                        className="flex items-center justify-between gap-3 border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 rounded-lg px-4 py-3 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex flex-col gap-1 min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                              {c.concepto}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 capitalize shrink-0 ml-2">
                              {c.periodo}
                            </span>
                          </div>
                          <span className="text-xs text-gray-400 dark:text-gray-500 px-0.5">
                            {formatFecha(c.fecha_emision)} ·{' '}
                            <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                              {c.monto != null
                                ? `${c.monto.toFixed(2).replace('.', ',')} €`
                                : '0,00 €'}
                            </span>
                            {c.ruta_storage ? (
                              <span className="ml-1.5 text-[10px] font-semibold text-[#103254] dark:text-blue-400">
                                · Factura
                              </span>
                            ) : (
                              <span className="ml-1.5 text-[10px] font-semibold text-gray-400 dark:text-gray-500">
                                · Comprobante
                              </span>
                            )}
                          </span>
                          <span className="text-[10px] font-mono text-gray-300 dark:text-gray-600 px-0.5 truncate">
                            {c.numero}
                          </span>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDescargar(c)}
                          className="h-8 w-8 p-0 rounded-lg text-gray-400 hover:text-[#103254] hover:bg-blue-50 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 transition-colors shrink-0"
                          title={`Descargar ${c.concepto} — ${c.periodo}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                // ── PESTAÑA MOVIMIENTOS ───────────────────────────────────────
                movimientosFiltrados.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <History className="w-8 h-8 text-gray-200 dark:text-gray-700 mb-2" />
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      {movimientos.length === 0
                        ? 'No hay movimientos registrados todavía.'
                        : 'No hay resultados para ese filtro.'}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {movimientosFiltrados.map((m, idx) => {
                      const info = infoMovimiento(m.tipo_movimiento);
                      const { Icon } = info;
                      const esPlan = m.ambito === 'plan';
                      return (
                        <div
                          key={`${m.ambito}-${m.fecha_movimiento}-${idx}`}
                          className="flex items-start gap-3 border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 rounded-lg px-4 py-3"
                        >
                          <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${info.cls}`} />

                          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                                {info.label}
                              </span>
                              <span
                                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${
                                  esPlan
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-[#103254] dark:text-blue-400'
                                    : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                                }`}
                              >
                                {esPlan ? 'Plan' : 'Trabajador'}
                              </span>
                            </div>

                            {/* Detalle: nombre del plan o del trabajador */}
                            {(esPlan ? m.detalle : m.trabajador) && (
                              <span className="text-xs text-gray-600 dark:text-gray-300 truncate">
                                {esPlan ? m.detalle : m.trabajador}
                              </span>
                            )}

                            <span className="text-[11px] text-gray-400 dark:text-gray-500">
                              {formatFechaHora(m.fecha_movimiento)}
                            </span>

                            {m.observaciones && (
                              <span className="text-[11px] text-gray-400 dark:text-gray-500 italic truncate">
                                {m.observaciones}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>

            {/* Pie */}
            {pestana === 'facturas' && facturasFiltradas.length > 0 && (
              <div className="px-5 py-3 border-t border-gray-100 dark:border-white/10">
                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                  {facturasFiltradas.some((c) => !c.ruta_storage)
                    ? 'Los marcados como "Comprobante" no constituyen factura fiscal; los marcados como "Factura" sí lo son, emitidas por Stripe en nuestro nombre.'
                    : 'Facturas emitidas por Stripe en nuestro nombre.'}
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}